import { ClickOutsideContext, type ClickOutsideContextListener } from "@common/contexts/ClickOutside";
import { FormFieldContext } from "@common/contexts/FormField";
import { type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { Common } from "@common/types";
import { isEqual, isFunction } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Deferred from "./deferred";
import { uuid } from "./misc";

export const useAsyncSource = <T>(
  fn: T | (() => Promise<T> | T),
  deps: any[]
): [T | null, React.Dispatch<React.SetStateAction<T>>, boolean] => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);

    const mixed = isFunction(fn) ? fn() : fn;
    if (mixed instanceof Promise) {
      mixed.then((r) => !isCancelled && setData(r)).finally(() => setIsLoading(false));
    } else {
      setData(mixed);
      setIsLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, deps);

  return [data, setData, isLoading];
};

export const useClickOutside = (name: string, data: ClickOutsideContextListener) => {
  const context = useContext(ClickOutsideContext);
  const id = useMemo(() => `${name}-${uuid()}`, []);

  useEffect(() => {
    if (context) {
      context.subscribe(id, data);
    }

    return () => {
      if (context) {
        context.unsubscribe(id);
      }
    };
  }, [id]);

  useUpdated(() => {
    context.resubscribe(id, data);
  }, [id, data.callback, data.cannotClose, ...data.ignoreRefs]);
};

export const useIsMounted = () => {
  const isMounted = React.useRef(false);

  React.useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};

export const useMemoDeep = <T>(get: () => T, deps: any[]): T => {
  const [value, setValue] = useState(get());

  useEffect(() => {
    const newValue = get();
    if (!isEqual(newValue, value)) {
      setValue(newValue);
    }
  }, [...deps, value]);

  return value;
};

export const useLoadingOptions = (isLoading: boolean, options: Common.OptionItem<any>[]) => {
  const context = useContext(FormFieldContext);

  useEffect(() => {
    if (!context) return;

    context.onLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!context) return;

    context.onOptionsLoaded(options);
  }, [options]);
};

export const useOptionValue = <T extends OptionItem<any>>(options: T[]) => {
  const [value, setValue] = useState<any>(options[0]?.value);

  const previousOptions = usePrevious(options);
  useUpdated(() => {
    if (previousOptions !== options) {
      if (!options.some((x) => x.value === value)) {
        setValue(options[0]?.value);
      }
    }
  }, [options, previousOptions, value]);

  return [value, setValue];
};

/**
 * Returns a function that triggers the callback.
 *
 * e.g.
 *   const fn = useCallback(
 *    () => {
 *     // uses props.value
 *    },
 *    [props.value]
 *   )
 *
 *   const a = useCallback(
 *    () => {
 *      // call a function that can modifies props.value
 *
 *      fn(); // fn won't receive updated props.value
 *    },
 *    [fn])
 *
 *   fix -->
 *
 *   const triggerFn = useTrigger(fn);
 *
 *   // call triggerFn in a
 *
 * @param fn
 * @returns
 */
export const useTrigger = (fn: () => void) => {
  const [hash, setHash] = useState(uuid());

  const previousHash = usePrevious(hash);
  useUpdated(() => {
    if (previousHash && hash !== previousHash) {
      fn();
    }
  }, [hash, previousHash, fn]);

  return useCallback(() => {
    setHash(uuid());
  }, [setHash]);
};

/**
 * Tracks previous state of a value.
 *
 * @param value Props, state or any other calculated value.
 * @returns Value from the previous render of the enclosing component.
 *
 * @example
 * function Component() {
 *   const [count, setCount] = useState(0);
 *   const prevCount = usePrevious(count);
 *   // ...
 *   return `Now: ${count}, before: ${prevCount}`;
 * }
 */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

type UserPromiseArgs<T> = {
  fn: ((...args: any[]) => Promise<T>) | Promise<T>;
  /** Used as deps, thus the array size and order must remain constant */
  args: any[];
  /** Milliseconds to wait after the last change in the args */
  delay?: number;
  /** Whether or not the fn should be executed */
  shouldLoad: boolean;
  /** Pass in to force reload */
  reload?: string | null;
};
export const usePromise = <T>(
  args: UserPromiseArgs<T>
): [T | null, boolean, React.Dispatch<React.SetStateAction<T>>, string | null, Promise<T> | null] => {
  const i18n = useI18n();

  const deferred = React.useRef<Deferred<T> | null>(null);
  const [deferredHash, setDeferredHash] = useState<string>();

  const [isLoading, setIsLoading] = useState(args.shouldLoad);
  const [data, setData] = useState<T | null>(null);
  const [reloadHash, setReloadHash] = useState<string | null>(args.shouldLoad ? uuid() : null);
  const callArgs = useValue(args.args);
  const reload = useValue(args.reload);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    deferred.current = args.shouldLoad ? new Deferred<T>() : null;
  }, [args.shouldLoad]);

  useUpdated(() => {
    if (!args.shouldLoad) return;

    let timeout;
    if (args.delay) {
      if (deferred.current?.isComplete) {
        deferred.current = new Deferred<T>();
        setDeferredHash(uuid());
      }
      timeout = setTimeout(() => setReloadHash(uuid()), args.delay);
    } else {
      setReloadHash(uuid());
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [args.shouldLoad, args.delay, reload, callArgs]);

  useEffect(() => {
    let isCancelled = false;

    if (reloadHash) {
      setIsLoading(true);
      setData(null);
      setError(null);

      let promise = isFunction(args.fn) ? args.fn(...args.args) : args.fn;

      promise
        .then((x) => {
          if (isCancelled) return;

          setData(x);
          deferred.current!.resolve(x);
        })
        .catch((x) => {
          const error = typeof x === "string" ? x : x.message ?? i18n.somethingWentWrong;
          setError(error);

          deferred.current!.reject(error);
        })
        .finally(() => {
          if (isCancelled) return;

          setIsLoading(false);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [reloadHash]);

  return [data, isLoading, setData, error, deferred?.current?.promise ?? null];
};

export const useMounted = () => {
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  return isMounted;
};

// simulates componentDidUpdate
export const useUpdated = (fn: () => void | (() => void), deps: any[]) => {
  const isMounted = useRef(false);

  useEffect(() => {
    let destructor;
    if (isMounted.current) {
      destructor = fn();
    }
    return () => {
      destructor && destructor();
    };
  }, deps);

  useEffect(() => {
    isMounted.current = true;
  }, []);
};

export const useToggle = (value: boolean): [boolean, () => void] => {
  const [isOn, setIsOn] = useState<boolean>(value);

  const toggle = useCallback(() => setIsOn(!isOn), [isOn]);

  return [isOn, toggle];
};

export const useVisible = (): [boolean, () => void, () => void] => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const hide = useCallback(() => setIsVisible(false), []);
  const show = useCallback(() => setIsVisible(true), []);

  return [isVisible, show, hide];
};

export const useVisibleModal = <T extends string | {}>(
  defaultValue: T | null = null
): [T | null, React.Dispatch<React.SetStateAction<T | null>>, () => void] => {
  const [modal, setModal] = useState<T | null>(defaultValue);

  const hideModal = useCallback(() => setModal(null), [setModal]);

  return [modal, setModal, hideModal];
};

export const useValue = <T>(value: T) => {
  const previousValue = usePrevious(value);
  const [val, setValue] = React.useState<T>(value);

  useUpdated(() => {
    if (!isEqual(previousValue, value)) {
      setValue(value);
    }
  }, [previousValue, value]);

  return val;
};
